import React, { useState, useEffect } from "react";
import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import { motion } from "framer-motion";
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaTruckFast,
  FaCarSide,
  FaToggleOn,
  FaToggleOff,
  FaCalendar,
  FaSearchengin,
} from "react-icons/fa6";
import moment from "moment";
import api from "../../services/api";

const CommissionReport = ({
  rentals,
  transfers,
  partner,
  onRentalsUpdate,
  onTransfersUpdate,
}) => {
  const [activeTab, setActiveTab] = useState("rentals"); // 'rentals' or 'transfers'
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [searchQuery, setSearchQuery] = useState("");
  const adminToken = localStorage.getItem("adminToken");
  const partnerToken = localStorage.getItem("partnerToken");

  // Filter rentals based on search query
  const filteredRentals = rentals.filter(
    (rental) =>
      rental.customer.firstName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      rental.customer.lastName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      rental.carName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter transfers based on search query
  const filteredTransfers = transfers
    ? transfers.filter(
        (transfer) =>
          transfer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transfer.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          transfer.pickupLocation
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          transfer.dropoffLocation
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())
      )
    : [];

  // Get rentals for the selected month
  const monthlyRentals = filteredRentals.filter(
    (rental) =>
      moment(rental.createdAt).format("YYYY-MM") ===
      selectedMonth.format("YYYY-MM")
  );

  // Get transfers for the selected month
  const monthlyTransfers = filteredTransfers.filter(
    (transfer) =>
      moment(transfer.createdAt).format("YYYY-MM") ===
      selectedMonth.format("YYYY-MM")
  );

  // Calculate statistics based on active tab
  const stats =
    activeTab === "rentals"
      ? {
          totalCount: monthlyRentals.length,
          totalCommission: monthlyRentals.reduce(
            (sum, rental) => sum + (rental.commissionAmount || 0),
            0
          ),
          totalBookingValue: monthlyRentals.reduce(
            (sum, rental) => sum + rental.totalPrice,
            0
          ),
          averageCommission: monthlyRentals.length
            ? monthlyRentals.reduce(
                (sum, rental) => sum + (rental.commissionAmount || 0),
                0
              ) / monthlyRentals.length
            : 0,
        }
      : {
          totalCount: monthlyTransfers.length,
          totalCommission: monthlyTransfers.reduce(
            (sum, transfer) => sum + (transfer.partnerCommission || 0),
            0
          ),
          totalBookingValue: monthlyTransfers.reduce(
            (sum, transfer) => sum + (transfer.priceTotal || 0),
            0
          ),
          averageCommission: monthlyTransfers.length
            ? monthlyTransfers.reduce(
                (sum, transfer) => sum + (transfer.partnerCommission || 0),
                0
              ) / monthlyTransfers.length
            : 0,
        };
  const handlePrevMonth = () => {
    setSelectedMonth((prev) => prev.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setSelectedMonth((prev) => prev.clone().add(1, "month"));
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchQuery("");
  };

  // Handle toggling commission payment status for rentals
  const handleRentalCommissionPaymentToggle = async (
    rentalId,
    currentStatus
  ) => {
    try {
      await api.rentals.updateCommissionPayment(rentalId, !currentStatus);
      // Call the parent's update function to refresh data
      onRentalsUpdate();
    } catch (error) {
      console.error("Error updating rental commission payment status:", error);
    }
  };

  // Handle toggling commission payment status for transfers
  const handleTransferCommissionPaymentToggle = async (
    transferId,
    currentStatus
  ) => {
    try {
      await api.contact.updateTransferCommissionPayment(
        transferId,
        !currentStatus
      );
      // Call the parent's update function to refresh data
      onTransfersUpdate();
    } catch (error) {
      console.error(
        "Error updating transfer commission payment status:",
        error
      );
    }
  };

  // Pay all unpaid commissions for the selected month based on active tab
  const handlePayAllMonth = async () => {
    try {
      if (activeTab === "rentals") {
        const unpaidRentals = monthlyRentals.filter(
          (rental) => !rental.commissionPaid
        );
        await Promise.all(
          unpaidRentals.map((rental) =>
            api.rentals.updateCommissionPayment(rental._id, true)
          )
        );
        onRentalsUpdate();
      } else {
        const unpaidTransfers = monthlyTransfers.filter(
          (transfer) => !transfer.commissionPaid
        );
        await Promise.all(
          unpaidTransfers.map((transfer) =>
            api.contact.updateTransferCommissionPayment(transfer._id, true)
          )
        );
        onTransfersUpdate();
      }
    } catch (error) {
      console.error("Error updating commission payments:", error);
    }
  };
  const downloadReport = () => {
    // Create a new Excel workbook
    const workbook = new ExcelJS.Workbook();

    if (activeTab === "rentals") {
      // RENTAL REPORT WORKSHEET
      const worksheet = workbook.addWorksheet("Rental Commission Report");

      // Set column widths for better readability
      worksheet.columns = [
        { header: "Date", key: "date", width: 14 },
        { header: "Customer", key: "customer", width: 25 },
        { header: "Car", key: "car", width: 25 },
        { header: "Rental Duration", key: "duration", width: 16 },
        { header: "Total Price", key: "price", width: 14 },
        { header: "Commission", key: "commission", width: 14 },
        { header: "Status", key: "status", width: 10 },
      ];

      // Add title and metadata with styling
      const titleRow = worksheet.addRow([
        `Rental Commission Report for ${partner.firstName} ${partner.lastName}`,
      ]);
      titleRow.height = 30;
      titleRow.font = { size: 16, bold: true };
      worksheet.mergeCells(`A1:G1`);
      titleRow.alignment = { horizontal: "center", vertical: "middle" };

      const monthRow = worksheet.addRow([
        `Month: ${selectedMonth.format("MMMM YYYY")}`,
      ]);
      monthRow.font = { size: 12, bold: true };
      worksheet.mergeCells(`A2:G2`);
      monthRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add empty row for spacing
      worksheet.addRow([]);

      // Add header row with styling
      const headerRow = worksheet.addRow([
        "Date",
        "Customer",
        "Car",
        "Rental Duration",
        "Total Price",
        "Commission",
        "Status",
      ]);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" }, // White text
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      monthlyRentals.forEach((rental, index) => {
        const row = worksheet.addRow([
          moment(rental.createdAt).format("DD/MM/YYYY"),
          `${rental.customer.firstName} ${rental.customer.lastName}`,
          rental.carName,
          `${Math.ceil(
            moment(rental.returnDateTime).diff(
              moment(rental.pickupDateTime),
              "days"
            )
          )} days`,
          rental.totalPrice.toFixed(2),
          (rental.commissionAmount || 0).toFixed(2),
          rental.commissionPaid ? "Paid" : "Unpaid",
        ]);

        if (index % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F0FF" },
            };
          });
        }

        // Center align duration column
        row.getCell(4).alignment = { horizontal: "center" };

        // Right align and format currency columns
        row.getCell(5).alignment = { horizontal: "right" };
        row.getCell(5).numFmt = "€#,##0.00";
        row.getCell(6).alignment = { horizontal: "right" };
        row.getCell(6).numFmt = "€#,##0.00";

        // Center align status
        row.getCell(7).alignment = { horizontal: "center" };

        // Add borders to all cells
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Add total row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "TOTAL",
        monthlyRentals
          .reduce((sum, rental) => sum + rental.totalPrice, 0)
          .toFixed(2),
        monthlyRentals
          .reduce((sum, rental) => sum + (rental.commissionAmount || 0), 0)
          .toFixed(2),
        "",
      ]);

      // Style total row
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "double" },
          right: { style: "thin" },
        };

        if (colNumber === 4) {
          cell.alignment = { horizontal: "right" };
        }

        if (colNumber === 5 || colNumber === 6) {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "€#,##0.00";
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
    } else {
      // TRANSFER REPORT WORKSHEET
      const worksheet = workbook.addWorksheet("Transfer Commission Report");

      // Set column widths for better readability
      worksheet.columns = [
        { header: "Date", key: "date", width: 14 },
        { header: "Client", key: "client", width: 25 },
        { header: "From → To", key: "route", width: 30 },
        { header: "Service Type", key: "serviceType", width: 16 },
        { header: "Total Price", key: "price", width: 14 },
        { header: "Commission", key: "commission", width: 14 },
        { header: "Status", key: "status", width: 10 },
      ];

      // Add title and metadata with styling
      const titleRow = worksheet.addRow([
        `Transfer Commission Report for ${partner.firstName} ${partner.lastName}`,
      ]);
      titleRow.height = 30;
      titleRow.font = { size: 16, bold: true };
      worksheet.mergeCells(`A1:G1`);
      titleRow.alignment = { horizontal: "center", vertical: "middle" };

      const monthRow = worksheet.addRow([
        `Month: ${selectedMonth.format("MMMM YYYY")}`,
      ]);
      monthRow.font = { size: 12, bold: true };
      worksheet.mergeCells(`A2:G2`);
      monthRow.alignment = { horizontal: "center", vertical: "middle" };

      // Add empty row for spacing
      worksheet.addRow([]);

      // Add header row with styling
      const headerRow = worksheet.addRow([
        "Date",
        "Client",
        "From → To",
        "Service Type",
        "Total Price",
        "Commission",
        "Status",
      ]);

      // Style header row
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }, // Blue background
        };
        cell.font = {
          bold: true,
          color: { argb: "FFFFFF" }, // White text
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // Add data rows
      monthlyTransfers.forEach((transfer, index) => {
        const row = worksheet.addRow([
          moment(transfer.createdAt).format("DD/MM/YYYY"),
          `${transfer.name} ${transfer.surname}`,
          `${transfer.pickupLocation} → ${transfer.dropoffLocation}`,
          transfer.serviceType || "One-way",
          (transfer.priceTotal || 0).toFixed(2),
          (transfer.partnerCommission || 0).toFixed(2),
          transfer.commissionPaid ? "Paid" : "Unpaid",
        ]);

        if (index % 2 === 1) {
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "E6F0FF" },
            };
          });
        }

        // Center align service type column
        row.getCell(4).alignment = { horizontal: "center" };

        // Right align and format currency columns
        row.getCell(5).alignment = { horizontal: "right" };
        row.getCell(5).numFmt = "€#,##0.00";
        row.getCell(6).alignment = { horizontal: "right" };
        row.getCell(6).numFmt = "€#,##0.00";

        // Center align status
        row.getCell(7).alignment = { horizontal: "center" };

        // Add borders to all cells
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      // Add total row
      const totalRow = worksheet.addRow([
        "",
        "",
        "",
        "TOTAL",
        monthlyTransfers
          .reduce((sum, transfer) => sum + (transfer.priceTotal || 0), 0)
          .toFixed(2),
        monthlyTransfers
          .reduce((sum, transfer) => sum + (transfer.partnerCommission || 0), 0)
          .toFixed(2),
        "",
      ]);

      // Style total row
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "double" },
          right: { style: "thin" },
        };

        if (colNumber === 4) {
          cell.alignment = { horizontal: "right" };
        }

        if (colNumber === 5 || colNumber === 6) {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "€#,##0.00";
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "D9E1F2" },
          };
        }
      });
    }

    // Generate the Excel file and trigger download
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const reportType = activeTab === "rentals" ? "rental" : "transfer";
      FileSaver.saveAs(
        blob,
        `${reportType}-commission-report-${selectedMonth.format(
          "YYYY-MM"
        )}.xlsx`
      );
    });
  };

  return (
    <div className="bg-gradient-to-r from-gray-600 to-gray-800 rounded-lg shadow-sm border border-gray-200">
      <div className="border-b bg-gradient-to-r from-gray-600 to-gray-800 border-gray-600 rounded-lg px-6 py-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-300">
            Monthly Commission Report
          </h3>
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaChevronLeft className="w-4 h-4 text-gray-200" />
            </button>
            <div className="flex items-center">
              <FaCalendar className="w-4 h-4 text-gray-200 mr-2" />
              <span className="text-sm font-medium text-gray-200">
                {selectedMonth.format("MMMM YYYY")}
              </span>
            </div>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaChevronRight className="w-4 h-4 text-gray-200" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 border-b border-gray-200">
          <div className="flex space-x-8">
            <button
              onClick={() => handleTabChange("rentals")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "rentals"
                  ? "border-blue-400 text-blue-400"
                  : "border-transparent text-gray-200 hover:text-gray-400 hover:border-gray-300"
              } transition-colors duration-150 ease-in-out flex items-center`}
            >
              <FaCarSide className="mr-2" />
              Car Rentals
            </button>
            <button
              onClick={() => handleTabChange("transfers")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "transfers"
                  ? "border-blue-400 text-blue-400"
                  : "border-transparent text-gray-200 hover:text-gray-400 hover:border-gray-300"
              } transition-colors duration-150 ease-in-out flex items-center`}
            >
              <FaTruckFast className="mr-2" />
              Transfers
            </button>
          </div>
        </div>
      </div>{" "}
      <div className="p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-sm text-blue-600 font-medium">
              {activeTab === "rentals" ? "Total Rentals" : "Total Transfers"}
            </p>
            <p className="text-2xl font-bold text-blue-900">
              {stats.totalCount}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-sm text-green-600 font-medium">
              Total Commission
            </p>
            <p className="text-2xl font-bold text-green-900">
              €{stats.totalCommission.toFixed(2)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-sm text-purple-600 font-medium">
              Total Booking Value
            </p>
            <p className="text-2xl font-bold text-purple-900">
              €{stats.totalBookingValue.toFixed(2)}
            </p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4">
            <p className="text-sm text-yellow-600 font-medium">
              Average Commission
            </p>
            <p className="text-2xl font-bold text-yellow-900">
              €{stats.averageCommission.toFixed(2)}
            </p>
          </div>
        </div>{" "}
        {/* Search and Actions Bar */}
        <div className="flex justify-between items-center mb-4">
          <div className="relative w-64">
            <input
              type="text"
              placeholder={
                activeTab === "rentals"
                  ? "Search rentals..."
                  : "Search transfers..."
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <FaSearchengin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
          <div className="flex items-center space-x-4">
            {adminToken && (
              <button
                onClick={handlePayAllMonth}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Pay Month
              </button>
            )}
            <button
              onClick={downloadReport}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaDownload className="w-4 h-4 mr-2" />
              Download Report
            </button>
          </div>
        </div>{" "}
        {/* Tables - Conditional rendering based on active tab */}
        {activeTab === "rentals" ? (
          /* Rentals Table */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Car
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyRentals.map((rental) => (
                  <tr key={rental._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {moment(rental.createdAt).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rental.customer.firstName} {rental.customer.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rental.carName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.ceil(
                        moment(rental.returnDateTime).diff(
                          moment(rental.pickupDateTime),
                          "days"
                        )
                      )}{" "}
                      days
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      €{rental.totalPrice.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      €{(rental.commissionAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() =>
                          handleRentalCommissionPaymentToggle(
                            rental._id,
                            rental.commissionPaid
                          )
                        }
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          rental.commissionPaid
                            ? "bg-green-100 text-green-800 hover:bg-green-200"
                            : "bg-red-100 text-red-800 hover:bg-red-200"
                        }`}
                      >
                        {rental.commissionPaid ? "Paid" : "Unpaid"}
                      </button>
                    </td>
                  </tr>
                ))}
                {monthlyRentals.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No rentals found for this month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          /* Transfers Table */
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From → To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Booking Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthlyTransfers.length > 0 ? (
                  monthlyTransfers.map((transfer) => (
                    <tr key={transfer._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {moment(transfer.createdAt).format("DD/MM/YYYY")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.name} {transfer.surname}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span>{transfer.pickupLocation}</span>
                          <span className="mx-2">→</span>
                          <span>{transfer.dropoffLocation}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transfer.serviceType === "one-way" 
                          ? "One-way" 
                          : transfer.serviceType === "round-trip" 
                            ? "Round-trip" 
                            : transfer.serviceType || "One-way"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        €{(transfer.priceTotal || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        €{(transfer.partnerCommission || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {adminToken && (
                          <button
                            onClick={() =>
                              handleTransferCommissionPaymentToggle(
                                transfer._id,
                                transfer.commissionPaid
                              )
                            }
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                              transfer.commissionPaid
                                ? "bg-green-100 text-green-800 hover:bg-green-200"
                                : "bg-red-100 text-red-800 hover:bg-red-200"
                            }`}
                          >
                            {transfer.commissionPaid ? "Paid" : "Unpaid"}
                          </button>
                        )}
                        {!adminToken && (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            transfer.commissionPaid
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}>
                            {transfer.commissionPaid ? "Paid" : "Unpaid"}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            transfer.status === "confirmed"
                              ? "bg-green-100 text-green-800"
                              : transfer.status === "cancelled"
                                ? "bg-red-100 text-red-800"
                                : transfer.status === "completed"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {transfer.status
                            ? transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1)
                            : "Pending"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No transfers found for this month
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionReport;
